import { Box, Stack } from '@mui/material';
import { ScreenHeader } from '../ScreenHeader/ScreenHeader';
import { DetailsField } from './components/DetailsField';
import { DetailsPanel } from './components/DetailsPanel';
import { DetailsSection } from './components/DetailsSection/DetailsSection';
import DetailsSectionPendencyAlert from './components/DetailsSectionPendencyAlert';
import { DetailsToggle } from './components/DetailsToggle/DetailsToggle';
import { ObjectContent } from './components/ObjectContent';
import { ObjectControls } from './components/ObjectControls';
import { ObjectFilterBar } from './components/ObjectFilterBar';
import { ObjectSlot } from './components/ObjectSlot';
import { ObjectStickyToolbar } from './components/ObjectStickyToolbar';
import { ObjectTabs } from './components/ObjectTabs/ObjectTabs';
import { ObjectTab } from './components/ObjectTabs/components/ObjectTab';
import { ObjectPendenciesProvider } from './contexts/ObjectPendencies.context';
import { ObjectScreenContext } from './contexts/ObjectScreen.context';
import { useObjectScreenLayout } from './hooks/useObjectScreenLayout';
import { useObjectScreenState } from './hooks/useObjectScreenState';
import { ObjectScreenProps } from './objectScreen.types';

const ObjectScreenRoot = ({
  objectId,
  objectType,
  defaultTab,
  isDetailsOpen,
  defaultDetailsOpen,
  isDetailsPanelInlineOnTablet,
  onDetailsOpenChange,
  hasDetailsToggle = true,
  detailsToggleAlert,
  hasTabScopedFilters,
  hasStickyFilterBar,
  children,
}: ObjectScreenProps) => {
  const {
    contextValue,
    setControlsNode,
    setFilterBarNode,
    scrollParent,
    stickyToolbar,
    setScrollParent,
    setStickyToolbar,
    setStickyFilterBar,
    isDetailsOpen: isDetailsPanelOpen,
    toggleDetails,
    setDetailsExpansionSteps,
  } = useObjectScreenState({
    objectId,
    objectType,
    defaultTab,
    isDetailsOpen,
    defaultDetailsOpen,
    hasTabScopedFilters,
    onDetailsOpenChange,
  });

  const {
    header,
    tabs,
    content,
    panel,
    afterHeaderSlots,
    afterToolbarSlots,
    afterFilterBarSlots,
    Layout,
  } = useObjectScreenLayout(children);

  const detailsToggle =
    panel && hasDetailsToggle ? (
      <DetailsToggle
        isActive={isDetailsPanelOpen}
        alert={detailsToggleAlert}
        onClick={toggleDetails}
      />
    ) : null;

  const tabsRow = (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.5 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{tabs}</Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box ref={setControlsNode} sx={{ display: 'flex', alignItems: 'center', gap: 1 }} />
        {detailsToggle}
      </Stack>
    </Stack>
  );

  return (
    <ObjectScreenContext.Provider
      value={{ ...contextValue, isDetailsOpen: Boolean(panel) && isDetailsPanelOpen }}
    >
      <Layout
        objectType={objectType}
        header={header}
        content={content}
        afterHeaderSlots={afterHeaderSlots}
        afterToolbarSlots={afterToolbarSlots}
        tabsRow={tabsRow}
        filterBar={<Box ref={setFilterBarNode} />}
        afterFilterBarSlots={afterFilterBarSlots}
        hasStickyFilterBar={hasStickyFilterBar}
        scrollParent={scrollParent}
        stickyToolbar={stickyToolbar}
        setScrollParent={setScrollParent}
        setStickyToolbar={setStickyToolbar}
        setStickyFilterBar={setStickyFilterBar}
        details={{
          panel,
          isOpen: isDetailsPanelOpen,
          isInlineOnTablet: isDetailsPanelInlineOnTablet,
          onClose: toggleDetails,
          onExpansionStepsChange: setDetailsExpansionSteps,
        }}
      />
    </ObjectScreenContext.Provider>
  );
};

export const ObjectScreen = Object.assign(ObjectScreenRoot, {
  Header: ScreenHeader,
  Tabs: ObjectTabs,
  Tab: ObjectTab,
  Content: ObjectContent,
  Controls: ObjectControls,
  FilterBar: ObjectFilterBar,
  DetailsPanel,
  DetailsToggle,
  Section: DetailsSection,
  SectionPendencyAlert: DetailsSectionPendencyAlert,
  Field: DetailsField,
  Slot: ObjectSlot,
  StickyToolbar: ObjectStickyToolbar,
  PendenciesProvider: ObjectPendenciesProvider,
});

export default ObjectScreen;
